import { CreatorProductModule } from '@/api/CreatorProductModuleApi';
import { SofaModule, SerializedSofaModule } from './objects/SofaModule';
import { ProjectRenderer } from './ProjectRenderer';
import { UUID } from '@/types/types';
import { ConnectingSlot, SlotSide, SlotType } from './objects/ConnectingSlot';
import { RENDERER_CONFIG } from '@/configs/rendererConfig';
import { AssetsLib } from './AssetsLib';
import { ThreeEvent } from './types';
import { Vector3 } from 'three';
import { ProductModule } from '@/api/ProductModuleApi';

export type SerializedModulesManager = {
	sofaModules: SerializedSofaModule[];
};

export class ModulesManager {
	private assetsLib = AssetsLib.getInstance();
	sofaModulesMap = new Map<UUID, SofaModule>();

	constructor(private renderer: ProjectRenderer) {}

	private isSlotOnSide(slot: ConnectingSlot, side: SlotSide) {
		if (slot.slotSide === side) return true;
		if (side === SlotSide.LEFT) return slot.slotType === SlotType.LEFT;
		if (side === SlotSide.RIGHT) return slot.slotType === SlotType.RIGHT;
		return false;
	}

	private getNextModuleBySide(
		sofaModule: SofaModule,
		side: SlotSide,
		visited: Set<UUID>
	) {
		const slot = sofaModule.connectingSlots.find(
			(slot) =>
				this.isSlotOnSide(slot, side) &&
				!!slot.connectedSlot &&
				!visited.has(slot.connectedSlot.hostModule.id)
		);
		if (!slot?.connectedSlot) return null;

		return slot.connectedSlot.hostModule;
	}

	private traverseBySide(
		sofaModule: SofaModule,
		side: SlotSide,
		callback: (sofaModule: SofaModule) => void
	) {
		const visited = new Set<UUID>();
		let currentModule: SofaModule | null = sofaModule;

		while (currentModule) {
			if (visited.has(currentModule.id)) break;

			callback(currentModule);
			visited.add(currentModule.id);
			currentModule = this.getNextModuleBySide(currentModule, side, visited);
		}
	}

	private connectNearbySlots(sofaModule: SofaModule) {
		const freeSlots = sofaModule.connectingSlots.filter(
			(s) => !s.connectedSlot
		);
		const slotPosition = new Vector3();
		const otherPosition = new Vector3();

		for (const freeSlot of freeSlots) {
			freeSlot.point.getWorldPosition(slotPosition);

			let closestSlot: ConnectingSlot | null = null;
			let closestDistance: number =
				RENDERER_CONFIG.CONNECT_NEARBY_SLOT_THRESHOLD;

			for (const [id, otherModule] of this.sofaModulesMap) {
				if (id === sofaModule.id) continue;

				for (const otherSlot of otherModule.connectingSlots) {
					if (!freeSlot.canConnectTo(otherSlot)) continue;

					otherSlot.point.getWorldPosition(otherPosition);
					const distance = slotPosition.distanceTo(otherPosition);

					if (distance >= closestDistance) continue;

					closestSlot = otherSlot;
					closestDistance = distance;
				}
			}

			if (closestSlot) freeSlot.connectTo(closestSlot);
		}
	}

	private getSlotByType(sofaModule: SofaModule, slotType: SlotType) {
		return sofaModule.connectingSlots.find(
			(slot) => slot.slotType === slotType
		);
	}

	private shiftRightChain(startModule: SofaModule) {
		let currentModule: SofaModule | null = startModule;

		while (currentModule) {
			const currentRightSlot = this.getSlotByType(
				currentModule,
				SlotType.RIGHT
			);
			if (!currentRightSlot) return;

			const nextSlot = currentRightSlot.connectedSlot;
			if (!nextSlot) return;

			const nextModule = nextSlot.hostModule;
			const nextLeftSlot = this.getSlotByType(nextModule, SlotType.LEFT);
			if (!nextLeftSlot) return;

			nextModule.alignToSlot(currentRightSlot, nextLeftSlot);
			currentModule = nextModule;
		}
	}

	setOrder() {
		const firstModule = this.sofaModulesMap.values().next().value;
		if (!firstModule) {
			this.renderer.emitter.emit(
				ThreeEvent.SET_CREATOR_PRODUCT_MODULES_ORDER,
				[]
			);
			return;
		}

		let edgeRightModule = firstModule;
		this.traverseBySide(edgeRightModule, SlotSide.RIGHT, (sofaModule) => {
			edgeRightModule = sofaModule;
		});

		const modulesOrder: UUID[] = [];
		this.traverseBySide(edgeRightModule, SlotSide.LEFT, (sofaModule) => {
			modulesOrder.push(sofaModule.id);
		});

		this.renderer.emitter.emit(
			ThreeEvent.SET_CREATOR_PRODUCT_MODULES_ORDER,
			modulesOrder
		);
	}

	async addSofaModule(creatorModule: CreatorProductModule, isFirst?: true) {
		const asset = creatorModule.productModule!.asset;
		if (!asset.model) throw new Error('MISSING_MODEL');

		const model =
			this.assetsLib.models.get(asset.model.path) ||
			(await this.assetsLib.loadModel(asset.model.path));

		const sofaModule = new SofaModule(
			this.renderer,
			asset,
			creatorModule.id,
			model.clone(true)
		);
		sofaModule.createConnectingSlots();

		this.addModuleToCollection(sofaModule);

		if (isFirst) return;

		sofaModule.connectBySlot();
		this.connectNearbySlots(sofaModule);
		this.setOrder();
	}

	async swapSofaModule(id: UUID, productModule: ProductModule) {
		const sofaModule = this.sofaModulesMap.get(id);
		if (!sofaModule) throw new Error('SOFA_MODULE_NOT_FOUND');

		const oldLeftSlot = this.getSlotByType(sofaModule, SlotType.LEFT);
		const oldRightSlot = this.getSlotByType(sofaModule, SlotType.RIGHT);
		const leftNeighborSlot = oldLeftSlot?.connectedSlot ?? null;
		const rightNeighborSlot = oldRightSlot?.connectedSlot ?? null;

		const asset = productModule.asset;
		if (!asset.model) throw new Error('MISSING_MODEL');

		const model =
			this.assetsLib.models.get(asset.model.path) ||
			(await this.assetsLib.loadModel(asset.model.path));

		sofaModule.swapModel(asset, model.clone(true));
		sofaModule.createConnectingSlots();

		const newLeftSlot = this.getSlotByType(sofaModule, SlotType.LEFT);
		const newRightSlot = this.getSlotByType(sofaModule, SlotType.RIGHT);

		if (leftNeighborSlot && newLeftSlot)
			newLeftSlot.connectTo(leftNeighborSlot);
		if (rightNeighborSlot && newRightSlot)
			newRightSlot.connectTo(rightNeighborSlot);

		const hasLeft = !!leftNeighborSlot && !!newLeftSlot;
		const hasRight = !!rightNeighborSlot && !!newRightSlot;

		if (!hasLeft && !hasRight) {
			this.setOrder();
			this.renderer.modulesChangeSideEffects();
			return;
		}

		if (hasLeft && !hasRight) {
			sofaModule.alignToSlot(leftNeighborSlot!, newLeftSlot!);
			this.setOrder();
			this.renderer.modulesChangeSideEffects();
			return;
		}

		if (!hasLeft && hasRight) {
			sofaModule.alignToSlot(rightNeighborSlot!, newRightSlot!);
			this.setOrder();
			this.renderer.modulesChangeSideEffects();
			return;
		}

		sofaModule.alignToSlot(leftNeighborSlot!, newLeftSlot!);
		this.shiftRightChain(sofaModule);
		this.setOrder();
		this.renderer.modulesChangeSideEffects();
	}

	async addFromData(moduleData: SerializedSofaModule) {
		let model = this.assetsLib.models.get(moduleData.asset.model!.path);
		if (!model)
			model = await this.assetsLib.loadModel(moduleData.asset.model!.path);

		const sofaModule = new SofaModule(
			this.renderer,
			moduleData.asset,
			moduleData.id,
			model.clone(true)
		);
		await sofaModule.createConnectingSlots(moduleData);

		sofaModule.group.position.fromArray(moduleData.objectData.position);
		sofaModule.group.rotation.fromArray(moduleData.objectData.rotation);

		for (const targetName in moduleData.materialActions) {
			const action = moduleData.materialActions[targetName];
			this.renderer.actionHandler.dispatchAction(action, sofaModule);
		}

		this.addModuleToCollection(sofaModule);

		return sofaModule;
	}

	getAllSlots() {
		const slots: ConnectingSlot[] = [];

		this.sofaModulesMap.forEach((sofaModule) => {
			slots.push(...sofaModule.connectingSlots);
		});

		return slots;
	}

	addModuleToCollection(sofaModule: SofaModule) {
		this.sofaModulesMap.set(sofaModule.id, sofaModule);
		this.renderer.collection.add(sofaModule.group);
	}

	removeSofaModule(creatorModule: CreatorProductModule) {
		const sofaModule = this.sofaModulesMap.get(creatorModule.id);
		if (!sofaModule) return;

		sofaModule.dispose();
		this.sofaModulesMap.delete(sofaModule.id);
		this.setOrder();
	}

	clearCollection() {
		this.sofaModulesMap.forEach((sofaModule) => {
			sofaModule.dispose();
		});

		this.sofaModulesMap.clear();
	}

	export(): SerializedModulesManager {
		return {
			sofaModules: [...this.sofaModulesMap.values()].map((sofaModule) =>
				sofaModule.export()
			),
		};
	}
}
